import React, {useState, useEffect} from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  ProgressBar,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaCircle,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUsers,
  FaEye,
  FaReply,
} from "react-icons/fa";

const RealTimeMetrics = ({campaigns = [], isLive = true}) => {
  const [metrics, setMetrics] = useState({
    messagesInQueue: 0,
    deliveryRate: 98.5,
    readRate: 85.2,
    responseRate: 21.4,
    avgResponseTime: "2.3 horas",
    activeUsers: 0,
    systemStatus: "operational", // operational, degraded, down
    lastUpdate: new Date(),
  });

  const [trends, setTrends] = useState({
    deliveryRate: 2.1,
    readRate: -1.3,
    responseRate: 5.7,
    activeUsers: 12,
  });

  // Simular actualizaciones en tiempo real
  useEffect(() => {
    if (!isLive) return;

    const interval = setInterval(() => {
      setMetrics((prev) => ({
        ...prev,
        messagesInQueue: Math.max(
          0,
          prev.messagesInQueue + Math.floor(Math.random() * 10 - 5)
        ),
        deliveryRate: Math.min(
          100,
          Math.max(90, prev.deliveryRate + (Math.random() - 0.5) * 2)
        ),
        readRate: Math.min(
          100,
          Math.max(70, prev.readRate + (Math.random() - 0.5) * 3)
        ),
        responseRate: Math.min(
          50,
          Math.max(10, prev.responseRate + (Math.random() - 0.5) * 2)
        ),
        activeUsers: Math.max(
          0,
          prev.activeUsers + Math.floor(Math.random() * 6 - 3)
        ),
        lastUpdate: new Date(),
      }));

      // Actualizar tendencias ocasionalmente
      if (Math.random() < 0.3) {
        setTrends((prev) => ({
          deliveryRate: (Math.random() - 0.5) * 10,
          readRate: (Math.random() - 0.5) * 8,
          responseRate: (Math.random() - 0.5) * 12,
          activeUsers: Math.floor((Math.random() - 0.5) * 20),
        }));
      }
    }, 3000); // Actualizar cada 3 segundos

    return () => clearInterval(interval);
  }, [isLive]);

  const getStatusColor = (status) => {
    switch (status) {
      case "operational":
        return "success";
      case "degraded":
        return "warning";
      case "down":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "operational":
        return "Operacional";
      case "degraded":
        return "Degradado";
      case "down":
        return "Fuera de Servicio";
      default:
        return "Desconocido";
    }
  };

  const getTrendIcon = (trend) => {
    if (trend > 1) return <FaArrowUp className="text-success" />;
    if (trend < -1) return <FaArrowDown className="text-danger" />;
    return <FaMinus className="text-muted" />;
  };

  const MetricCard = ({
    title,
    value,
    unit = "",
    trend,
    icon,
    color = "primary",
  }) => (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body className="d-flex align-items-center">
        <div className={`text-${color} me-3`} style={{fontSize: "2rem"}}>
          {icon}
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center justify-content-between">
            <div className="fw-bold fs-4">
              {value}
              {unit}
            </div>
            {trend !== undefined && (
              <div className="d-flex align-items-center">
                {getTrendIcon(trend)}
                <span
                  className={`small ms-1 ${
                    trend > 1
                      ? "text-success"
                      : trend < -1
                      ? "text-danger"
                      : "text-muted"
                  }`}
                >
                  {Math.abs(trend).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
          <div className="text-muted small text-uppercase">{title}</div>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <div>
      {/* Status Header */}
      <Row className="mb-3">
        <Col>
          <Alert
            variant={getStatusColor(metrics.systemStatus)}
            className="d-flex align-items-center justify-content-between mb-0"
          >
            <div className="d-flex align-items-center">
              <FaCircle className="me-2" style={{fontSize: "0.8rem"}} />
              <strong>
                Estado del Sistema: {getStatusText(metrics.systemStatus)}
              </strong>
              {metrics.messagesInQueue > 0 && (
                <Badge bg="info" className="ms-3">
                  {metrics.messagesInQueue} mensajes en cola
                </Badge>
              )}
            </div>
            <div className="d-flex align-items-center">
              {isLive && (
                <Spinner animation="grow" size="sm" className="me-2" />
              )}
              <small>
                Última actualización: {metrics.lastUpdate.toLocaleTimeString()}
              </small>
            </div>
          </Alert>
        </Col>
      </Row>

      {/* Métricas en Tiempo Real */}
      <Row className="mb-4">
        <Col md={3}>
          <MetricCard
            title="Tasa de Entrega"
            value={metrics.deliveryRate.toFixed(1)}
            unit="%"
            trend={trends.deliveryRate}
            icon={<FaCheckCircle />}
            color="success"
          />
        </Col>
        <Col md={3}>
          <MetricCard
            title="Tasa de Lectura"
            value={metrics.readRate.toFixed(1)}
            unit="%"
            trend={trends.readRate}
            icon={<FaEye />}
            color="primary"
          />
        </Col>
        <Col md={3}>
          <MetricCard
            title="Tasa de Respuesta"
            value={metrics.responseRate.toFixed(1)}
            unit="%"
            trend={trends.responseRate}
            icon={<FaReply />}
            color="warning"
          />
        </Col>
        <Col md={3}>
          <MetricCard
            title="Usuarios Activos"
            value={metrics.activeUsers}
            trend={trends.activeUsers}
            icon={<FaUsers />}
            color="info"
          />
        </Col>
      </Row>

      {/* Información Adicional */}
      <Row>
        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h6 className="fw-bold mb-0">
                <FaClock className="me-2 text-primary" />
                Tiempo de Respuesta
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Promedio General</span>
                <span className="fw-bold">{metrics.avgResponseTime}</span>
              </div>
              <ProgressBar
                now={75}
                variant="success"
                style={{height: "8px"}}
                className="mb-3"
              />

              <div className="small text-muted">
                <div className="d-flex justify-content-between mb-1">
                  <span>Rápido (&lt; 1h)</span>
                  <span>45%</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span>Normal (1-4h)</span>
                  <span>35%</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Lento (&gt; 4h)</span>
                  <span>20%</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h6 className="fw-bold mb-0">
                <FaExclamationTriangle className="me-2 text-warning" />
                Alertas del Sistema
              </h6>
            </Card.Header>
            <Card.Body>
              {metrics.messagesInQueue > 50 && (
                <Alert variant="warning" className="py-2 mb-2">
                  <small>
                    <strong>Cola Alta:</strong> {metrics.messagesInQueue}{" "}
                    mensajes pendientes
                  </small>
                </Alert>
              )}

              {metrics.deliveryRate < 95 && (
                <Alert variant="danger" className="py-2 mb-2">
                  <small>
                    <strong>Tasa de Entrega Baja:</strong>{" "}
                    {metrics.deliveryRate.toFixed(1)}%
                  </small>
                </Alert>
              )}

              {metrics.readRate < 80 && (
                <Alert variant="info" className="py-2 mb-2">
                  <small>
                    <strong>Lectura Baja:</strong> Considera revisar horarios de
                    envío
                  </small>
                </Alert>
              )}

              {metrics.messagesInQueue <= 50 &&
                metrics.deliveryRate >= 95 &&
                metrics.readRate >= 80 && (
                  <Alert variant="success" className="py-2 mb-0">
                    <small>
                      <FaCheckCircle className="me-2" />
                      <strong>Todo funcionando correctamente</strong>
                    </small>
                  </Alert>
                )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RealTimeMetrics;
